import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { CaseAdmin, Patient } from "pwapi";

import {
  createQAReview,
  requestAdminCase,
  resetCaseErrorMessage,
  resetDeletionStatus,
  saveAdminCase,
  updateIntegrations,
} from "shared/actions/admin/adminCaseActions";
import { savePatient } from "shared/actions/admin/adminPatientActions";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import EditCasePanel from "./EditCasePanel";

const CaseEditController = () => {
  const case_id = parseInt(useParams<{ case_id: string }>().case_id || "0");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const slide_id = parseInt(useParams<{ slide_id: string }>().slide_id || "0");
  const specimen_id = parseInt(
    useParams<{ specimen_id: string }>().specimen_id || "0"
  );
  const documentQuery = useParams<{ document_id: string }>().document_id;
  const document_id = parseInt(documentQuery || "0");

  const dispatch = useDispatch();

  const rawAdminCase = useAppSelector(({ admin }) => admin.case);
  const adminCase = rawAdminCase
    ? {
        ...rawAdminCase,
        slides: rawAdminCase.slides.map((s: SlideAdminWithUrls) => ({
          ...s,
          case: rawAdminCase,
          specimen: rawAdminCase.specimens.find(
            (sp) => sp.id === s.specimen_id
          ),
          block_obj: rawAdminCase.blocks?.find((b) => b.id === s.block_obj_id),
        })),
      }
    : null;

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  useEffect(() => {
    dispatch(requestAdminCase(case_id));
  }, [case_id]);

  const onSavePatient = (newPatient: Patient) => {
    dispatch(savePatient({ patient: newPatient, adminCase_id: adminCase?.id }));
  };

  const onSaveCase = (newCase: Partial<CaseAdmin>) => {
    dispatch(saveAdminCase(newCase));
  };

  const onSaveIntegration = (payload: {
    caseId: number;
    clinicId: number;
    patientId: number;
    external_order_id?: string;
    external_ezderm_order_id?: string;
    external_patient_id?: string;
    orderingDoctorId?: number;
  }) => {
    dispatch(updateIntegrations(payload));
  };

  const onSaveQAReview = (payload: any) => {
    dispatch(createQAReview(payload));
  };

  const onResetErrorMessage = () => {
    dispatch(resetCaseErrorMessage());
  };

  const onResetDeletionStatus = () => {
    dispatch(resetDeletionStatus());
  };

  return adminCase ? (
    <EditCasePanel
      adminCase={adminCase}
      isDeleting={isDeleting}
      setIsDeleting={setIsDeleting}
      resetErrorMessage={onResetErrorMessage}
      resetDeletionStatus={onResetDeletionStatus}
      onSaveCase={onSaveCase}
      onSavePatient={onSavePatient}
      onSaveQAReview={onSaveQAReview}
      onSaveIntegration={onSaveIntegration}
      slideId={slide_id}
      documentId={document_id}
      documentUploading={documentQuery === "upload"}
      specimenId={specimen_id}
      features={features}
    />
  ) : (
    <Spinner />
  );
};

export default CaseEditController;
